import TabContext from "@mui/lab/TabContext";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import { Box, Tab } from "@mui/material";
import { createContext, ReactElement, SyntheticEvent, useState } from "react";
import { useParams } from "react-router-dom";

import ErrorCard from "~frontend/components/ErrorCard";

import { CampaignForm } from "./CampaignForm";
import { useTenDlcContext } from "./TenDlcProvider";
import { TCR_CAMPAIGN_ID_REGEX, UUID_REGEX } from "./idRegexes";

export const TabNavigationContext = createContext<(id: string) => void>(
  (_id: string) => {}
);

export const CampaignsTab = ({
  header,
}: {
  header: ReactElement;
}): ReactElement => {
  const { campaignRegistrationId: initialCampaignId } = useParams<{
    brandRegistrationId: string;
    campaignRegistrationId?: string;
  }>();

  const {
    brand: { campaignRegistrations },
  } = useTenDlcContext()!;

  const submittedCampaignRegistrations = campaignRegistrations.filter(
    campaign => !!campaign.submittedAt
  );

  let initialCampaignRegistrationId: string | null = null;
  if (initialCampaignId && UUID_REGEX.test(initialCampaignId)) {
    initialCampaignRegistrationId = initialCampaignId;
  } else if (
    initialCampaignId &&
    TCR_CAMPAIGN_ID_REGEX.test(initialCampaignId)
  ) {
    initialCampaignRegistrationId =
      submittedCampaignRegistrations.find(
        cr =>
          cr.tcrCampaignId?.toLowerCase() === initialCampaignId.toLowerCase()
      )?.id ?? null;
  }

  const [selectedCampaignRegId, setSelectedCampaignRegId] = useState(
    initialCampaignRegistrationId ?? campaignRegistrations[0]?.id
  );

  return (
    <Box>
      {header}
      <TabContext value={selectedCampaignRegId}>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <TabList
            variant="scrollable"
            onChange={(e: SyntheticEvent, newValue: string) => {
              setSelectedCampaignRegId(newValue);
            }}
          >
            {submittedCampaignRegistrations.map(campaign => (
              <Tab
                key={campaign.id}
                label={
                  campaign.friendlyName ??
                  campaign.tcrCampaignId ??
                  "Untitled Campaign"
                }
                value={campaign.id}
              />
            ))}
          </TabList>
        </Box>
        {initialCampaignId && !initialCampaignRegistrationId ? (
          <ErrorCard
            errors={[
              {
                message: `Campaign registration ID "${initialCampaignId}" is not a valid UUID or TCR ID`,
              },
            ]}
          />
        ) : (
          submittedCampaignRegistrations.map(campaign => (
            <TabPanel key={campaign.id} value={campaign.id} sx={{ padding: 0 }}>
              <TabNavigationContext value={setSelectedCampaignRegId}>
                <CampaignForm campaign={campaign} />
              </TabNavigationContext>
            </TabPanel>
          ))
        )}
      </TabContext>
    </Box>
  );
};
